import React from "react";
import PropTypes from "prop-types";
import { QRCode } from "antd";
import { dte, frases } from "../../../services";
import { formatCurrency, formatShownDate } from "./utils";

export const InvoiceNormalTemplate = ({ invoice }) => {
	const qrUrl = `https://felpub.c.sat.gob.gt/verificador-web/publico/vistas/verificacionDte.jsf?tipo=autorizacion&numero=${invoice?.certification?.authorization_number}&emisor=${invoice?.sender_id}&receptor=${invoice?.receptor_id}`;

	return (
		<html lang="es">
			<head>
				<meta charSet="UTF-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				<title>Factura</title>
				<style>
					{`
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 0;
              display: flex;
              flex-direction: column;
              min-height: 100vh;
              color: black; /* Color predeterminado para todo el texto */
              background-color: white;
            }
            .container {
              flex: 1;
              width: auto;
              background-color: #fff;
              padding: 40px;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
            .header, .footer, .invoice-info, .company-info {
              color: black; /* Texto asegurado como negro */
            }
            .invoice-details {
              display: flex;
              justify-content: space-between;
              padding-bottom: 20px;
              padding-top: 20px;
            }
            .company-info h3, .company-info h5, .company-info h4,
            .invoice-info h3, .invoice-info h5, .invoice-info h4 {
              margin: 0;
              line-height: 1.2;
              color: black; /* Texto asegurado como negro */
            }
            .invoice-details p {
              margin: 5px 0 0 0;
              font-size: 13px;
              color: black; /* Texto asegurado como negro */
            }
            .table {
              width: 100%;
              border-collapse: collapse;
            }
            .table th, .table td {
              padding: 4px;
              border-bottom: 2px solid whitesmoke;
              vertical-align: top;
              color: black; /* Asegurar color negro para celdas */
            }
            .table th {
              text-align: left;
              border-bottom: 3px solid whitesmoke;
              border-top: 3px solid whitesmoke;
              background-color: whitesmoke;
              color: black; /* Asegurar color negro para encabezados */
            }
            .table td {
              font-size: 13px;
              color: black; /* Texto asegurado como negro */
            }
            .taxes {
              display: block;
              font-size: 11px;
              color: black; /* Texto asegurado como negro */
            }
            .total {
              text-align: right;
              margin-top: 20px;
              color: black; /* Texto asegurado como negro */
            }
            .total h2 {
              margin: 0;
              color: black; /* Asegurar color negro */
            }
            .footer {
              font-size: 10px;
              text-align: center;
              color: black; /* Texto asegurado como negro */
            }
            .footer .certifier {
              font-weight: bold;
              color: black; /* Asegurar color negro */
            }
			.watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-30deg); /* Inclinación de 30 grados */
              font-size: 5em;
              color: rgba(255, 0, 0, 0.2);
              text-transform: uppercase;
              letter-spacing: 10px;
              white-space: nowrap;
              z-index: 0;
              pointer-events: none; /* Para que los demás elementos sean accesibles */
            }
          `}
				</style>
			</head>
			<body>
				<div className="container">
					{invoice?.canceled && <div className="watermark">Anulada</div>}
					<div>
						<div className="header">
							<h1>{Object.assign({}, ...Object.values(dte).flatMap(items => items.map(item => ({ [item.value]: item.label }))))[invoice?.type] || invoice?.type}</h1>
						</div>
						<div className="invoice-details">
							<div className="company-info">
								<h4>{invoice?.sender_name}</h4>
								<p>{invoice?.establishment_name}</p>
								<p>{invoice?.sender_id}</p>
								<p>{invoice?.sender_extras?.address}</p>
							</div>
							<div className="invoice-info">
								<h4>Fecha de Emisión: {formatShownDate(invoice?.date)}</h4>
								<p>Moneda: {invoice?.currency}</p>
							</div>
						</div>
						<div className="invoice-details">
							<div className="company-info">
								<h4>Datos del Cliente:</h4>
								<p>{invoice?.receptor_name}</p>
								<p>{invoice?.receptor_id}</p>
								<p>{invoice?.receptor_extras?.address}</p>
							</div>
							<div className="invoice-info">
								<p>{invoice?.certification?.authorization_number}</p>
								<p>Serie: {invoice?.reference}</p>
								<p>Número: {invoice?.number}</p>
							</div>
						</div>
						<table className="table">
							<thead>
								<tr>
									<th className="description">Descripción</th>
									<th className="quantity">Cantidad</th>
									<th className="unit-price">Precio Unitario</th>
									<th className="discount">Descuento</th>
									<th className="total">Total</th>
								</tr>
							</thead>
							<tbody>
								{invoice?.items.map((item) => (
									<tr key={item.id}>
										<td>{item.initial_description}</td>
										<td className="amount">{item.quantity}</td>
										<td className="amount">
											{formatCurrency(item.unit_price, invoice?.currency)}
										</td>
										<td className="amount">
											{formatCurrency(item.discount, invoice?.currency)}
										</td>
										<td className="amount">
											{formatCurrency(item.total, invoice?.currency)}
											{item.taxes.map((tax) => (
												<div className="taxes" key={tax.name}>
                          [ {tax.name} {formatCurrency(tax.total, invoice?.currency)} ]
												</div>
											))}
										</td>
									</tr>
								))}
							</tbody>
						</table>
						<div className="total">
							<h2>Total: {formatCurrency(invoice?.total, invoice?.currency)}</h2>
						</div>
						<div className="invoice-details">
							<div className="company-info">
								{invoice?.tax_authority_specifications.frases.map((frase) => (
									<p key={frase.type}>*{frases[frase.type][frase.scenario]}</p>
								))}
							</div>
							<div className="invoice-info">
								<QRCode
									type='svg'
									value={qrUrl}
									bgColor="#fff"
									size={100}
								/>
							</div>
						</div>
					</div>
					<div className="footer">
						<p>
							<span className="certifier">Datos del Certificador:</span> {invoice?.certification?.certifier_name}
							<span className="certifier">  Nit:</span> {invoice?.certification?.certifier_id}
						</p>
					</div>
				</div>
			</body>
		</html>
	);
};

InvoiceNormalTemplate.propTypes = {
	invoice: PropTypes.object.isRequired,
};